import { WalletSmartAccountType } from "@wallet-manager/node-types/dist/src/postgres/const";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import {
  amountDivideDecimals,
  findChainInfo,
  listMappingTransform,
} from "src/utils/helper";
import { GetListRes } from "../types/GetListRes";
import { TableColumnKey, TableRow } from "../types/Table";
import { useAssets } from "src/components/FetchConfig";

interface UseTableRowsProps {
  apiList: GetListRes[];
}

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te } = useTranslation(translatePrefix);
  const { apiList } = props;
  const { selectAssetsByChain } = useAssets();

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const assets = selectAssetsByChain({
        chain_type: item?.chain_type,
        chain_id: item?.chain_id,
      });
      const foundAsset = assets.find(
        (asset) => asset.asset_name === item?.asset_name,
      );

      const totalBalance = amountDivideDecimals(
        item?.total_amount,
        foundAsset?.decimals,
      );

      const thresholdTotalAmount = amountDivideDecimals(
        item.approve_allowance_asset_config
          ?.notification_threshold_total_amount,
        foundAsset?.decimals,
      );

      const isAmountLargerThanThreshold =
        Number(totalBalance) > Number(thresholdTotalAmount);

      const walletCount = item?.wallet_approve_allowances?.length;
      const thresholdWalletCount =
        item?.approve_allowance_asset_config.notification_threshold_count;
      const isWalletCountLargerThanThreshold =
        Number(walletCount) > Number(thresholdWalletCount);

      const isSmartAccount =
        item?.smart_account_type !== WalletSmartAccountType.EOA;

      const mappedResult = [
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          findChainInfo(item?.chain_type?.toString(), item.chain_id)?.name,
        ],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), item.asset_name],
        [
          TableColumnKey.SmartAccountType,
          t(TableColumnKey.SmartAccountType),
          isSmartAccount ? te("Yes") : te("No"),
        ],
        [
          TableColumnKey.WalletMajorVersion,
          t(TableColumnKey.WalletMajorVersion),
          item?.major_wallet_version.major_wallet_version,
        ],
        [
          TableColumnKey.WalletsCount,
          t(TableColumnKey.WalletsCount),
          walletCount,
        ],
        [
          TableColumnKey.TotalBalance,
          t(TableColumnKey.TotalBalance),
          totalBalance,
        ],
        [
          TableColumnKey.Threshold,
          t(TableColumnKey.Threshold),
          item?.approve_allowance_asset_config?.notification_threshold_count,
        ],
        [
          TableColumnKey.SpenderAddress,
          t(TableColumnKey.SpenderAddress),
          item?.spender_address,
        ],
        ["rawData", "", item],
        ["isAmountLargerThanThreshold", "", isAmountLargerThanThreshold],
        [
          "isWalletCountLargerThanThreshold",
          "",
          isWalletCountLargerThanThreshold,
        ],
        ["thresholdTotalAmount", "", thresholdTotalAmount],
        ["isSmartAccount", "", isSmartAccount],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    mapResultToTupleArray,
  };
};

export default useTableRows;
